import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";

import Apps from "./Apps";
import Spinner from "../../common/Spinner";
import CountChip from "../../common/CountChip";
import { listApps } from "../../../redux/actions/AppActions";

const AppListDetails = ({ from, organizationId }) => {
    const dispatch = useDispatch();

    const role = useSelector((state) => state.userLogin.userInfo.data.role);
    const appList = useSelector((state) => state.appList);
    const organization = useSelector(
        (state) => state.organizationDetails.organization,
    );
    const { apps, sortBy, requestedCount, existingCount, totalCount } = appList;
    const organizationIds =
        organizationId ?? organization?.organization_id;
    const organizationRoles = organization?.user_role;

    const isSuperAdmin = role === "Super_Admin";
    const isOrgAdmin = organizationRoles === "Admin";
    const isMemberOrTester =
        organizationRoles === "Member" || organizationRoles === "Tester";

    const [activeTab, setActiveTab] = useState("Existing");
    const [currentPage, setCurrentPage] = useState(0);
    const [os, setOs] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [appsPerPage, setAppsPerPage] = useState(20);
    const [loading, setLoading] = useState(false);

    const runListApps = (overrides = {}) => {
        if (!isSuperAdmin && !organizationIds) return;
        const args = {
            pageNum: currentPage + 1,
            appsPerPage,
            sortBy: sortBy || "",
            searchTerm,
            role,
            os,
            activeTab,
            from,
            organizationIds,
            ...overrides,
        };
        setLoading(true);
        dispatch(
            listApps(
                args.pageNum,
                args.appsPerPage,
                args.sortBy,
                args.searchTerm,
                args.role,
                args.os,
                args.activeTab,
                args.from,
                args.organizationIds,
            ),
        )
            .then(() => setLoading(false))
            .catch((err) => {
                console.error("Error fetching apps:", err);
                setLoading(false);
            });
    };

    useEffect(() => {
        runListApps({ pageNum: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationIds]);

    const handleChangeTab = (_event, newValue) => {
        setActiveTab(newValue);
        setCurrentPage(0);
        runListApps({ activeTab: newValue, pageNum: 1 });
    };

    const handleFilterBy = (e) => {
        const filterByOs = e.target.value;
        setOs(filterByOs);
        setCurrentPage(0);
        runListApps({ os: filterByOs, pageNum: 1 });
    };

    const handleSearchSubmit = (e) => {
        if (e.key === "Enter") {
            setCurrentPage(0);
            runListApps({ searchTerm, pageNum: 1 });
        }
    };

    const handlePageChange = (_event, newPage) => {
        setCurrentPage(newPage);
        runListApps({ pageNum: newPage + 1 });
    };

    const handleRowsPerPageChange = (e) => {
        const newPerPage = Number(e.target.value);
        setAppsPerPage(newPerPage);
        setCurrentPage(0);
        runListApps({ appsPerPage: newPerPage, pageNum: 1 });
    };

    const showTabs = isSuperAdmin || isOrgAdmin || isMemberOrTester;

    // Header count displayed above the table: for the "Existing" tab we use
    // existingCount from the API; for "Requested" we use requestedCount.
    const visibleCount =
        activeTab === "Existing" ? existingCount || 0 : requestedCount || 0;

    // Determine columns dynamically per role / tab to match legacy behavior.
    const existingColumns = [
        { key: "name", label: "Name" },
        { key: "logo", label: "Logo" },
        { key: "description", label: "Description" },
        { key: "os", label: "OS" },
        ...(from === "Apps" && !isSuperAdmin ? [{ key: "builds", label: "Builds", align: "center" }] : []),
        ...(isSuperAdmin ? [
            { key: "status", label: "Status" },
            { key: "builds", label: "Builds", align: "center" },
        ] : []),
        ...((!isSuperAdmin && isOrgAdmin) ? [
            { key: "status", label: "Status", align: "center" },
            { key: "action", label: "Action", align: "center" },
        ] : []),
    ];

    const requestedColumns = [
        { key: "name", label: "Name" },
        { key: "logo", label: "Logo" },
        { key: "description", label: "Description" },
        { key: "os", label: "OS" },
        ...(isSuperAdmin ? [{ key: "requestedBy", label: "Requested By" }] : []),
        ...((!isSuperAdmin && isOrgAdmin) ? [
            { key: "requestedBy", label: "Requested By" },
            { key: "action", label: "Action" },
        ] : []),
    ];

    const columns = activeTab === "Existing" ? existingColumns : requestedColumns;

    return (
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider" }}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
                    {from === "Apps" ? (
                        <Typography variant="h3" component="h1">Apps</Typography>
                    ) : <span />}
                </Stack>

                {showTabs && (
                    <Tabs
                        value={activeTab}
                        onChange={handleChangeTab}
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            mb: 2,
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
                        }}
                    >
                        <Tab
                            value="Existing"
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>Existing apps</span>
                                    <CountChip value={existingCount || 0} color="primary.main" />
                                </Stack>
                            }
                        />
                        <Tab
                            value="Requested"
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>Requested apps</span>
                                    <CountChip value={requestedCount || 0} color="warning.main" />
                                </Stack>
                            }
                        />
                    </Tabs>
                )}

                {/* Toolbar: OS filter + search + add */}
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="os-filter-label" shrink>Filter by OS</InputLabel>
                        <Select
                            labelId="os-filter-label"
                            label="Filter by OS"
                            notched
                            displayEmpty
                            value={os}
                            onChange={handleFilterBy}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="IOS">iOS</MenuItem>
                            <MenuItem value="Android">Android</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        placeholder="Search apps — press Enter"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchSubmit}
                        sx={{ flex: 1, minWidth: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ flex: { md: "0 0 auto" } }}>
                        {isOrgAdmin && !isSuperAdmin && (
                            <Button
                                component={RouterLink}
                                to="/app/add"
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Add app
                            </Button>
                        )}
                        {isMemberOrTester && !isSuperAdmin && (
                            <Button
                                component={RouterLink}
                                to="/app/request"
                                variant="outlined"
                                startIcon={<FeedbackOutlinedIcon />}
                            >
                                New app request
                            </Button>
                        )}
                    </Box>
                </Stack>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 12, background: "var(--ba-bg-soft, #FAFBFC)" } }}>
                                {columns.map((col) => (
                                    <TableCell key={col.key} align={col.align || "left"}>
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleCount > 0 && apps?.length > 0 ? (
                                apps.map((app) => (
                                    <Apps
                                        app={app}
                                        role={role}
                                        from={from}
                                        organizationId={organizationId}
                                        key={app.id}
                                    />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ color: "text.secondary", py: 6 }}>
                                        {activeTab === "Existing" ? "No apps yet." : "No requested apps."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount || visibleCount || 0}
                    page={currentPage}
                    onPageChange={handlePageChange}
                    rowsPerPage={appsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[20, 40, 60, 80, 100]}
                />
            </Paper>

            {loading && <Spinner fullscreen label="Loading apps…" />}
        </>
    );
};

export default AppListDetails;
